/** Schedule: what goes out next, the 14 days ahead, and the order of the queue. */
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getPiece, getSchedule, moveScheduled, unschedule, type PieceDetail, type ScheduleItem } from './api'
import { destinationChipClass, destinationLabel, etDayShort, etTime } from './labels'
import { pieceHref, piecesHref, settingsHref } from './links'
import { PublishDialog } from './piece/PublishDialog'
import { etDayStrip, openSlotLabel } from './schedule'
import { useEditorialTitle } from './titleContext'
import { describeError } from '../lib/describeError'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const card = 'rounded-xl border border-ink-500 bg-ink-700'
const btn = `inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-lg border border-ink-400 px-4 font-ui text-[14px] font-semibold text-paper hover:bg-ink-600 disabled:cursor-not-allowed disabled:opacity-50 ${focus}`
const iconBtn = `inline-flex h-10 w-10 items-center justify-center rounded-lg border border-ink-400 font-ui text-[15px] font-semibold text-paper hover:bg-ink-600 disabled:cursor-not-allowed disabled:opacity-40 ${focus}`
const link = `font-ui text-[14px] text-amber-glow underline underline-offset-2 hover:text-paper ${focus}`
const chipPlain = 'inline-flex h-6 items-center rounded-full bg-ink-600 px-2.5 font-ui text-[12px] font-semibold text-paper-dim'

export function SchedulePage() {
  const { section, publicationSlug } = useEditorialTitle()
  const qc = useQueryClient()
  const nav = useNavigate()
  const key = ['editorial', 'schedule', publicationSlug]
  const q = useQuery({ queryKey: key, queryFn: () => getSchedule(publicationSlug) })
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [publishing, setPublishing] = useState<PieceDetail | null>(null)
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: key })
    void qc.invalidateQueries({ queryKey: ['editorial', 'desk', publicationSlug] })
  }
  const move = useMutation({
    mutationFn: (v: { id: number; direction: 'up' | 'down' }) => moveScheduled(publicationSlug, v.id, v.direction),
    onSuccess: () => { setError(null); refresh() },
    onError: (e) => { setError(describeError(e)); refresh() },
  })
  const remove = useMutation({
    mutationFn: (id: number) => unschedule(id),
    onSuccess: () => { setError(null); refresh() },
    onError: (e) => { setError(describeError(e)); refresh() },
  })
  const openPublish = useMutation({
    mutationFn: (id: number) => getPiece(id),
    onSuccess: (d) => { setError(null); setPublishing(d) },
    onError: (e) => setError(describeError(e)),
  })

  if (q.isLoading) return <p className="font-ui text-paper-dim">Loading…</p>
  if (q.isError || !q.data) return <p role="alert" className="font-ui text-radar-red">{describeError(q.error)}</p>
  const d = q.data
  const strip = etDayStrip(d.days)
  const open = openSlotLabel(strip)
  const first = d.queue[0]?.slot_at ?? null

  return (
    <div className="flex flex-col gap-6 font-ui">
      <header className="flex flex-col gap-1.5">
        <div className="label">{section.label} · Schedule</div>
        <h1 className="m-0 font-serif text-[32px] font-normal leading-tight text-paper">Going out next</h1>
        <p className="m-0 text-[14px] text-paper-dim">
          {d.slots_text ? <>One piece per slot: {d.slots_text}. </> : 'No release slots are set yet. '}
          <Link to={settingsHref(section.path)} className={link}>Change the slots</Link>
        </p>
      </header>

      <div className={`${card} flex flex-col gap-1 px-[18px] py-3.5`} role="status">
        <span className={`text-[15px] font-semibold ${d.releases_on ? 'text-radar-green' : 'text-amber-glow'}`}>
          {d.releases_on ? 'Releases are on.' : `Releases are paused for ${section.label}.`}
        </span>
        <span className="text-[13px] text-paper-dim">
          {d.releases_on
            ? <>{first ? `The next piece goes out ${etDayShort(first)} at ${etTime(first)}. ` : ''}If a slot is missed by more than two hours, that piece waits for the next one.</>
            : 'Scheduled pieces wait until an administrator turns releases back on.'}
        </span>
      </div>

      {notice && <p role="status" className="m-0 text-[14px] text-radar-green">{notice}</p>}
      {error && <p role="alert" className="m-0 text-[13px] text-radar-red">{error}</p>}

      <section aria-label="The next 14 days">
        <ol className="m-0 grid list-none grid-cols-2 gap-2 p-0 sm:grid-cols-4 lg:grid-cols-7">
          {strip.map((s) => (
            <li key={s.date} className={`flex min-h-[84px] flex-col gap-1.5 rounded-[10px] px-3 py-2.5 ${s.state === 'open' ? 'border border-dashed border-ink-400 text-paper-dim' : 'border border-ink-500 bg-ink-700 text-paper'} ${s.state === 'none' ? 'opacity-60' : ''}`}>
              <span className="text-[12px] font-semibold text-paper-faint">{s.label}</span>
              {s.slots.length === 0 && <span className="text-[12px] text-paper-faint">No slot</span>}
              {s.slots.map((sl) => (
                <span key={sl.at} className="text-[13px] leading-snug">
                  {sl.piece ? <Link to={pieceHref(section.path, sl.piece.id)} className={`text-paper no-underline hover:underline ${focus}`}>{sl.piece.title}</Link> : 'Open'}
                </span>
              ))}
            </li>
          ))}
        </ol>
      </section>

      <section aria-labelledby="sched-order" className="flex flex-col gap-3">
        <h2 id="sched-order" className="label m-0">In order</h2>
        {d.queue.length === 0 && <p className="m-0 text-[14px] text-paper-dim">Nothing is scheduled.</p>}
        <ol className="m-0 flex list-none flex-col gap-3 p-0">
          {d.queue.map((it: ScheduleItem, i) => (
            <li key={it.id} className={`${card} flex flex-wrap items-center gap-x-5 gap-y-3 px-[18px] py-4`}>
              <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-2">
                <span className="text-[13px] font-semibold text-paper-dim">{it.slot_at ? `${etDayShort(it.slot_at)} ${etTime(it.slot_at)}` : 'No slot yet'}</span>
                <Link to={pieceHref(section.path, it.id)} className={`font-serif text-[20px] leading-tight text-paper no-underline hover:underline ${focus}`}>{it.title}</Link>
                <div className="flex flex-wrap gap-1.5">
                  <span className={destinationChipClass(it.destination)}>{destinationLabel(it.destination, section.label)}</span>
                  <span className={chipPlain}>{it.format}</span>
                  {it.no_credit && <span className="inline-flex h-6 items-center rounded-full bg-amber-glow/15 px-2.5 text-[12px] font-semibold text-amber-glow">No photo credit yet</span>}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" className={iconBtn} aria-label="Move earlier" disabled={i === 0 || move.isPending} onClick={() => move.mutate({ id: it.id, direction: 'up' })}>↑</button>
                <button type="button" className={iconBtn} aria-label="Move later" disabled={i === d.queue.length - 1 || move.isPending} onClick={() => move.mutate({ id: it.id, direction: 'down' })}>↓</button>
                <button type="button" className={btn} disabled={openPublish.isPending} onClick={() => openPublish.mutate(it.id)}>Publish now</button>
                <button type="button" className={btn} disabled={remove.isPending}
                  onClick={() => { if (window.confirm(`Take “${it.title}” off the schedule? It goes back to Approved.`)) remove.mutate(it.id) }}>
                  Take off the schedule
                </button>
              </div>
            </li>
          ))}
        </ol>
        {open && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-ink-400 px-[18px] py-4">
            <span className="text-[14px] text-paper-dim">{open}</span>
            <Link to={piecesHref(section.path, 'approved')} className={link}>Fill it from Approved</Link>
          </div>
        )}
      </section>

      {publishing && (
        <PublishDialog
          detail={publishing}
          onClose={() => setPublishing(null)}
          onBeforeSubmit={async () => true}
          onShowItem={() => nav(pieceHref(section.path, publishing.id))}
          onPublished={(m) => { setPublishing(null); setNotice(m); refresh() }}
        />
      )}
    </div>
  )
}
